import { Link } from "@/lib/router-compat";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  Breadcrumb, BreadcrumbList, BreadcrumbItem, BreadcrumbLink, BreadcrumbSeparator, BreadcrumbPage,
} from "@/components/ui/breadcrumb";
import {
  Shield, Clock, Trophy, Brain, Calculator, Image, Edit3, FileText,
  Layers, Palette, Users, ChevronRight, Mail, HelpCircle,
  FileCheck, Lock, MapPin, CheckCircle, Send, PenTool, AlertTriangle,
} from "lucide-react";
import sentinelforgeLogo from "@/assets/sentinelforge-logo.webp";
import { SUBSCRIPTION_TIERS } from "@/lib/subscription-tiers";
import BreadcrumbJsonLd from "@/components/seo/BreadcrumbJsonLd";
import FaqJsonLd from "@/components/seo/FaqJsonLd";
import PageMeta from "@/components/seo/PageMeta";
import ServiceJsonLd from "@/components/seo/ServiceJsonLd";
import { getIndustrySeo, OFFER_URL, PRICE_FROM_USD } from "@/lib/industry-seo";
import { motion } from "framer-motion";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: "easeOut" as const },
  }),
};

const securityFaq = [
  { q: "What is Sentinel Forge?", a: "Sentinel Forge is a SaaS platform built specifically for private security companies. It automates proposal creation, staffing calculations, and threat assessments so you can deliver polished, client-ready proposals in minutes rather than hours." },
  { q: "Do I need any technical experience to use it?", a: "Not at all. Sentinel Forge is designed to be intuitive for security professionals, not software developers. Fill in the details about a site or client and the platform handles the rest." },
  { q: "Is there a free trial?", a: "Yes. The Starter plan includes a free 7-day trial so you can explore the platform with no commitment or credit card required upfront." },
  { q: "Can I add team members to my account?", a: "Yes. Agency-tier subscribers can add up to 5 team members who can collaborate on proposals. Enterprise plans support larger teams with custom limits." },
  { q: "How does the client portal work?", a: "Once you've created a proposal, you can share it with your client via a secure, branded portal link. Clients can review the proposal, leave feedback, and approve or reject it — all without needing an account." },
  { q: "What payment methods do you accept?", a: "All payments are securely processed through Stripe. We accept all major credit and debit cards. Subscriptions renew automatically each billing cycle and can be canceled at any time." },
  { q: "Can I export proposals to PDF?", a: "Yes. Every proposal can be exported as a professional PDF, ready to send directly to clients or print for in-person meetings." },
  { q: "What is white-labeling?", a: "Enterprise subscribers can fully white-label the platform — removing all Sentinel Forge branding and replacing it with their own company name, logo, and color scheme. Agency subscribers can add their company logo directly to proposals before generating them." },
  { q: "How do I cancel my subscription?", a: "You can cancel at any time from your account settings. Your access continues until the end of your current billing period. We do not offer refunds for partial billing periods." },
  { q: "How do I get support?", a: "You can reach our support team at support@sentinelforge.com. We also have an in-app support ticket system available to all subscribers." },
];

const SecurityIndustry = () => {
  const securitySeo = getIndustrySeo("security");
  const securityBreadcrumbs = [
    { name: "Home", url: "https://www.sentinelforgesystems.com/" },
    { name: "Industries", url: "https://www.sentinelforgesystems.com/industries" },
    { name: "Security", url: "https://www.sentinelforgesystems.com/industries/security" },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <BreadcrumbJsonLd items={securityBreadcrumbs} />
      <FaqJsonLd items={securityFaq} />
      <ServiceJsonLd
        name="Security"
        serviceType={securitySeo.serviceType}
        description={securitySeo.serviceDescription}
        url="https://www.sentinelforgesystems.com/industries/security"
        image={securitySeo.ogImage}
        priceFrom={PRICE_FROM_USD}
        offerUrl={OFFER_URL}
        breadcrumbs={securityBreadcrumbs}
      />
      <PageMeta
        title="Security Proposal Software — SentinelForge"
        description="Create professional security proposals, threat assessments, and contracts in minutes. AI-powered proposal builder for private security companies."
        url="https://www.sentinelforgesystems.com/industries/security"
        image={securitySeo.ogImage}
      />
      {/* NAV */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-border/50 bg-background/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={sentinelforgeLogo} alt="Sentinel Forge" className="h-7 w-7 object-contain" />
            <span className="text-lg font-bold text-foreground tracking-tight">Sentinel Forge</span>
          </Link>
          <div className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
            <Link to="/industries" className="hover:text-foreground transition-colors">Industries</Link>
            <a href="#features" className="hover:text-foreground transition-colors">Features</a>
            <a href="#pricing" className="hover:text-foreground transition-colors">Pricing</a>
            <a href="#faq" className="hover:text-foreground transition-colors">FAQ</a>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/auth">
              <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">Sign In</Button>
            </Link>
            <Link to="/auth?signup&industry=security">
              <Button size="sm" className="gradient-primary text-primary-foreground shadow-glow hover:opacity-90">Get Started</Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* BREADCRUMB */}
      <div className="pt-20 max-w-7xl mx-auto px-6">
        <Breadcrumb className="mb-0">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild><Link to="/">Home</Link></BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild><Link to="/industries">Industries</Link></BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Security</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      {/* HERO */}
      <section className="relative pt-12 pb-20 md:pt-16 md:pb-32 overflow-hidden">
        <div className="absolute inset-0 gradient-dark" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-primary/5 blur-3xl" />
        <div className="absolute top-20 right-20 w-72 h-72 rounded-full bg-primary/3 blur-2xl" />

        <div className="relative max-w-5xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-primary/30 bg-primary/10 text-primary text-sm font-medium mb-8">
              <Shield className="h-4 w-4" />
              Built for Private Security Companies
            </div>

            <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-foreground leading-[1.1] mb-6">
              Professional Security Proposals{" "}
              <span className="text-gradient">in Minutes</span>
            </h1>

            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-6 leading-relaxed">
              Sentinel Forge Systems helps security companies create polished, client-ready
              proposals with speed, accuracy, and consistency.
            </p>

            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-primary/20 bg-primary/5 text-sm text-muted-foreground mb-8">
              <Mail className="h-4 w-4 text-primary" />
              <span>Demos available — <a href="mailto:support@sentinelforge.com" className="text-primary font-medium hover:underline">request a preview</a> to see the platform in action</span>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/auth?signup&industry=security">
                <Button size="lg" className="gradient-primary text-primary-foreground shadow-glow hover:opacity-90 px-8 text-base h-12">
                  Get Started <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/pricing">
                <Button size="lg" variant="outline" className="border-border text-foreground hover:bg-secondary px-8 text-base h-12">
                  View Pricing
                </Button>
              </Link>
            </div>
            <p className="text-sm text-muted-foreground mt-4">Free 7-day trial included with the Starter plan</p>
          </motion.div>
        </div>
      </section>

      {/* VALUE PROPOSITIONS */}
      <section className="py-20 md:py-28 border-t border-border/50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Clock, title: "Save Hours of Work", description: "Automated proposal creation and staffing calculations eliminate manual busywork so you can focus on winning business." },
              { icon: Trophy, title: "Win More Contracts", description: "Clean, standardized, professional proposals that impress clients and set you apart from the competition." },
              { icon: Shield, title: "Built for Security Companies", description: "Designed by industry professionals with real operational experience in private security." },
            ].map((item, i) => (
              <motion.div key={item.title} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-50px" }} variants={fadeUp}>
                <Card className="border-border bg-card shadow-card h-full hover:border-primary/30 transition-colors duration-300">
                  <CardContent className="p-8">
                    <div className="h-12 w-12 rounded-xl gradient-accent flex items-center justify-center mb-5">
                      <item.icon className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground mb-3">{item.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{item.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="py-20 md:py-28 border-t border-border/50 bg-secondary/20">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0} className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Everything You Need to <span className="text-gradient">Close Deals</span>
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              From AI-powered threat analysis to branded PDF exports, Sentinel Forge handles the entire proposal workflow.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: AlertTriangle, label: "Threat Assessment Builder", description: "Document site vulnerabilities, access points, lighting, camera coverage, and incident history in a guided multi-step flow." },
              { icon: Brain, label: "AI-Powered Threat Analysis", description: "Generate comprehensive threat reports using AI and FBI crime statistics — risk matrices, vulnerability scoring, and prioritized mitigations." },
              { icon: Calculator, label: "Staffing & Cost Calculators", description: "Multiple pricing models with real-time cost projections for hourly, daily, weekly, and contract-based engagements." },
              { icon: FileText, label: "Proposal & PDF Export", description: "Executive-ready security proposals with branded PDF exports, editable sections, and professional formatting." },
              { icon: Image, label: "Site Photo Evidence", description: "Upload and annotate site photos to strengthen proposals with visual documentation of security conditions." },
              { icon: Edit3, label: "Editable AI Sections", description: "Every AI-generated section is fully editable — tailor the output to match your voice and client expectations." },
              { icon: Palette, label: "White-Label Branding", description: "Add your company logo, colors, and contact info for fully branded client-facing documents." },
              { icon: Users, label: "Team & Multi-User Access", description: "Invite team members, manage roles, and collaborate on proposals and threat assessments." },
              { icon: Layers, label: "Threat-to-Proposal Import", description: "Import saved threat assessments directly into proposals — AI analysis carries over to produce richer, data-driven security plans." },
            ].map((feat, i) => (
              <motion.div key={feat.label} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-30px" }} variants={fadeUp}>
                <Card className="border-border bg-card shadow-card h-full hover:border-primary/30 transition-colors duration-300">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="h-10 w-10 rounded-lg gradient-accent flex items-center justify-center shrink-0">
                        <feat.icon className="h-5 w-5 text-primary" />
                      </div>
                      <span className="text-sm font-semibold text-foreground">{feat.label}</span>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">{feat.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING PREVIEW */}
      <section id="pricing" className="py-20 md:py-28 border-t border-border/50">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0} className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Simple, Transparent <span className="text-gradient">Pricing</span>
            </h2>
            <p className="text-muted-foreground text-lg max-w-xl mx-auto">Plans that scale with your business. Start small, grow fast.</p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {(Object.entries(SUBSCRIPTION_TIERS) as [string, typeof SUBSCRIPTION_TIERS[keyof typeof SUBSCRIPTION_TIERS]][]).filter(([_, tier]) => !('internal' in tier && tier.internal)).map(([key, tier], i) => {
              const isPro = key === "professional";
              return (
                <motion.div key={key} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-30px" }} variants={fadeUp}>
                  <Card className={`border-border bg-card shadow-card h-full relative ${isPro ? "border-primary/50 shadow-glow" : ""}`}>
                    {isPro && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full gradient-primary text-primary-foreground text-xs font-semibold">Most Popular</div>
                    )}
                    <CardContent className="p-6 flex flex-col h-full">
                      <h3 className="text-lg font-semibold text-foreground mb-1">{tier.name}</h3>
                      <div className="mb-5">
                        {tier.price !== null ? (
                          <div className="flex items-baseline gap-1">
                            <span className="text-3xl font-bold text-foreground">${tier.price}</span>
                            <span className="text-muted-foreground text-sm">/mo</span>
                          </div>
                        ) : (
                          <span className="text-2xl font-bold text-foreground">Custom</span>
                        )}
                      </div>
                      <ul className="space-y-2.5 flex-1 mb-6">
                        {tier.features.slice(0, 4).map((f) => (
                          <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
                            <FileCheck className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                            {f}
                          </li>
                        ))}
                      </ul>
                      <Link to="/auth" className="w-full">
                        <Button
                          variant={isPro ? "default" : "outline"}
                          className={`w-full ${isPro ? "gradient-primary text-primary-foreground shadow-glow hover:opacity-90" : "border-border text-foreground hover:bg-secondary"}`}
                        >
                          Get Started
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>

          <div className="text-center mt-10">
            <Link to="/pricing">
              <Button variant="link" className="text-primary hover:text-primary/80">
                View full pricing details <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* EXAMPLES CAROUSEL */}
      <section className="py-20 md:py-28 border-t border-border/50">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0} className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              See What You&apos;ll <span className="text-gradient">Create</span>
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto mb-2">
              Here's a quick dashboard overview of what your proposals and contracts look like inside Sentinel Forge.
            </p>
            <p className="text-sm text-muted-foreground/70 max-w-xl mx-auto">
              These are summary cards — full proposals include detailed threat assessments, staffing plans, implementation timelines, and more.
            </p>
          </motion.div>

          {/* Proposals row */}
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-30px" }} variants={fadeUp} custom={1} className="mb-10">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">Example Proposals</h3>
            <div className="flex gap-5 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide" style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
              {[
                { address: "450 Commerce Blvd, Dallas, TX", type: "Retail Mall", risk: "High", summary: "High foot traffic retail complex with recent theft incidents. Recommends 3 armed officers with CCTV integration and access control.", officers: 3, hours: 12, days: 7, rate: 32 },
                { address: "1200 Industrial Park Dr, Houston, TX", type: "Warehouse", risk: "Moderate", summary: "Large distribution warehouse with perimeter vulnerabilities. Recommends 2 unarmed officers with patrol routes and alarm monitoring.", officers: 2, hours: 8, days: 5, rate: 25 },
                { address: "88 Residential Tower Ln, Austin, TX", type: "Residential Complex", risk: "Low", summary: "Gated residential community with existing access controls. Recommends 1 concierge officer for lobby presence and visitor management.", officers: 1, hours: 10, days: 7, rate: 22 },
              ].map((ex, i) => (
                <Card key={i} className="border-border bg-card shadow-card min-w-[320px] max-w-[340px] snap-start shrink-0">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                        ex.risk === "High" ? "bg-destructive/10 text-destructive" :
                        ex.risk === "Moderate" ? "bg-warning/20 text-warning" :
                        "bg-success/20 text-success"
                      }`}>{ex.risk} Risk</span>
                      <FileText className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <CardTitle className="text-base mt-2">{ex.type}</CardTitle>
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <MapPin className="h-3 w-3" /> {ex.address}
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-sm text-secondary-foreground leading-relaxed">{ex.summary}</p>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="bg-secondary/50 rounded-md p-2">
                        <p className="text-muted-foreground">Officers</p>
                        <p className="font-semibold text-foreground">{ex.officers}</p>
                      </div>
                      <div className="bg-secondary/50 rounded-md p-2">
                        <p className="text-muted-foreground">Coverage</p>
                        <p className="font-semibold text-foreground">{ex.hours}h × {ex.days}d</p>
                      </div>
                      <div className="bg-secondary/50 rounded-md p-2">
                        <p className="text-muted-foreground">Rate</p>
                        <p className="font-semibold text-foreground">${ex.rate}/hr</p>
                      </div>
                      <div className="bg-secondary/50 rounded-md p-2">
                        <p className="text-muted-foreground">Est. Weekly</p>
                        <p className="font-semibold text-primary">${(ex.officers * ex.hours * ex.days * ex.rate).toLocaleString()}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </motion.div>

          {/* Contracts row */}
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-30px" }} variants={fadeUp} custom={2}>
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">Example Contracts</h3>
            <div className="flex gap-5 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide" style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
              {[
                { title: "Security Services Agreement — Retail Mall", client: "Commerce Plaza Management LLC", status: "signed", date: "Jan 15, 2025", value: "$6,720/week", Icon: CheckCircle, statusColor: "bg-success/20 text-success" },
                { title: "Guard Services Contract — Warehouse", client: "Southwest Distribution Inc.", status: "sent", date: "Feb 3, 2025", value: "$2,000/week", Icon: Send, statusColor: "bg-info/20 text-info" },
                { title: "Concierge Security — Residential", client: "Skyline Towers HOA", status: "draft", date: "Feb 10, 2025", value: "$1,540/week", Icon: PenTool, statusColor: "bg-muted text-muted-foreground" },
              ].map((c, i) => (
                <Card key={i} className="border-border bg-card shadow-card min-w-[300px] max-w-[320px] snap-start shrink-0">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <span className={`text-xs font-medium px-2 py-1 rounded-full capitalize ${c.statusColor}`}>{c.status}</span>
                      <c.Icon className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <CardTitle className="text-base mt-2">{c.title}</CardTitle>
                    <p className="text-xs text-muted-foreground">{c.client}</p>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">{c.date}</span>
                      <span className="font-semibold text-primary">{c.value}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 md:py-28 border-t border-border/50 bg-secondary/20">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0} className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Frequently Asked <span className="text-gradient">Questions</span>
            </h2>
            <p className="text-muted-foreground text-lg">Everything you need to know before getting started.</p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-30px" }} variants={fadeUp} custom={1}>
            <Accordion type="single" collapsible className="space-y-3">
              {securityFaq.map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border border-border bg-card rounded-xl px-6 data-[state=open]:border-primary/30 transition-colors duration-300">
                  <AccordionTrigger className="text-foreground font-medium text-left hover:no-underline py-5">{item.q}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-5">{item.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 border-t border-border/50">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Ready to streamline your proposals?</h2>
          <p className="text-muted-foreground text-lg mb-8">Join hundreds of security companies already using Sentinel Forge to close deals faster.</p>
          <Link to="/auth?signup&industry=security">
            <Button size="lg" className="gradient-primary text-primary-foreground shadow-glow hover:opacity-90 px-10 text-base h-12">
              Get Started <ChevronRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
          <p className="text-sm text-muted-foreground mt-4">Free 7-day trial included with the Starter plan</p>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-border/50 bg-card/50 py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <img src={sentinelforgeLogo} alt="Sentinel Forge" className="h-5 w-5 object-contain" />
              <span className="text-sm font-semibold text-foreground">Sentinel Forge Systems</span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground">
              <a href="mailto:support@sentinelforge.com" className="hover:text-foreground transition-colors flex items-center gap-1">
                <Mail className="h-3.5 w-3.5" /> Contact
              </a>
              <a href="mailto:support@sentinelforge.com" className="hover:text-foreground transition-colors flex items-center gap-1">
                <HelpCircle className="h-3.5 w-3.5" /> Support
              </a>
              <Link to="/terms" className="hover:text-foreground transition-colors flex items-center gap-1">
                <FileText className="h-3.5 w-3.5" /> Terms of Service
              </Link>
              <Link to="/privacy" className="hover:text-foreground transition-colors flex items-center gap-1">
                <Lock className="h-3.5 w-3.5" /> Privacy Policy
              </Link>
            </div>
          </div>
          <div className="text-center mt-8">
            <p className="text-xs text-muted-foreground">
              SentinelForge is expanding. More industries will be added soon.
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              © {new Date().getFullYear()} Sentinel Forge Systems. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SecurityIndustry;
